<template>
  <v-card class="mb-8">
    <v-card-title>
      チャージ設定
      <v-spacer />
      <v-btn
        color="primary"
        text
        :ripple="false"
        @click="openDialog()"
      >
        ＋ 追加
      </v-btn>
    </v-card-title>

    <v-card-text>
      <p class="text-caption text--secondary">
        卓作成時の人数が条件に一致すると、会計全体にチャージ(率)を自動適用します。卓作成画面で率の変更・不適用の選択ができます。レシート・伝票では「サービス料等」に合算して表示されます。
      </p>

      <v-simple-table v-if="chargeSettings.length > 0">
        <template #default>
          <thead>
            <tr>
              <th>表示名</th>
              <th>率</th>
              <th>適用人数</th>
              <th>税サ</th>
              <th>丸め</th>
              <th class="text-right" />
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="chargeSetting in chargeSettings"
              :key="chargeSetting.id"
            >
              <td>{{ chargeSetting.name }}</td>
              <td>{{ toPercent(chargeSetting.rate) }}%</td>
              <td>{{ chargeSetting.customerCount }}名</td>
              <td>{{ taxChargeLabel(chargeSetting.taxCharge) }}</td>
              <td>{{ roundingLabel(chargeSetting) }}</td>
              <td class="text-right">
                <v-btn
                  text
                  small
                  color="primary"
                  :ripple="false"
                  @click="openDialog(chargeSetting)"
                >
                  編集
                </v-btn>
                <v-btn
                  text
                  small
                  color="error"
                  :ripple="false"
                  @click="destroy(chargeSetting)"
                >
                  削除
                </v-btn>
              </td>
            </tr>
          </tbody>
        </template>
      </v-simple-table>
      <p
        v-else
        class="text-body-2 text--secondary mb-0"
      >
        設定はありません
      </p>
    </v-card-text>

    <v-dialog
      v-model="isDialogOpen"
      max-width="640"
    >
      <v-card>
        <v-card-title>{{ editing.id ? 'チャージ設定を編集' : 'チャージ設定を追加' }}</v-card-title>
        <v-card-text>
          <v-row>
            <v-col
              cols="12"
              sm="6"
            >
              <v-text-field
                v-model="editing.name"
                label="表示名"
                placeholder="シングルチャージ"
                :error="!editing.name"
              />
            </v-col>
            <v-col
              cols="6"
              sm="3"
            >
              <v-text-field
                v-model.number="editing.ratePercent"
                label="率"
                suffix="%"
                type="number"
                min="0"
                :error="!(editing.ratePercent >= 0)"
              />
            </v-col>
            <v-col
              cols="6"
              sm="3"
            >
              <v-text-field
                v-model.number="editing.customerCount"
                label="適用人数"
                suffix="名"
                type="number"
                min="1"
                :error="!(editing.customerCount >= 1)"
              />
            </v-col>
          </v-row>

          <v-row>
            <v-col
              cols="12"
              sm="6"
            >
              <v-select
                v-model="editing.taxCharge"
                label="チャージの税サ"
                :items="taxChargeItems"
              />
              <p class="text-caption text--secondary mb-0">
                「通常」は合計に消費税10%が掛かります(税サ込み・カット運用の店舗向け)。明細で税サを取る運用と併用すると二重課税になるためご注意ください。
              </p>
            </v-col>
            <v-col
              cols="6"
              sm="3"
            >
              <v-select
                v-model="editing.roundingMethod"
                label="丸め処理"
                :items="roundingMethodItems"
              />
            </v-col>
            <v-col
              cols="6"
              sm="3"
            >
              <v-text-field
                v-if="editing.roundingMethod !== 'ceil_to_multiple'"
                :value="Math.abs(editing.roundingDigit)"
                label="丸める位(10^n円)"
                type="number"
                min="0"
                @input="editing.roundingDigit = -1 * Math.abs(Number($event) || 0)"
              />
              <v-text-field
                v-else
                v-model.number="editing.roundingMultiple"
                label="倍数(円)"
                type="number"
                min="0"
              />
            </v-col>
          </v-row>

          <p class="text-caption text--secondary mb-0">
            ここで設定する丸めはチャージ額に対してのみ適用されます。請求合計の丸め(大計の端数処理)はホール毎の会計設定に従います。
          </p>

          <div class="charge-preview pa-4 mt-2">
            <v-row dense>
              <v-col
                cols="12"
                sm="4"
              >
                <v-text-field
                  v-model.number="previewPrice"
                  label="試算: 会計合計(税サ込み)"
                  suffix="円"
                  type="number"
                  hide-details
                  dense
                />
              </v-col>
              <v-col
                cols="12"
                sm="8"
                class="d-flex align-center"
              >
                <span class="text-body-2">
                  チャージ +{{ formatPrice(preview.chargeAmount) }} 円 → 請求合計 <span class="font-weight-bold">{{ formatPrice(preview.total) }} 円</span>
                </span>
              </v-col>
            </v-row>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            :ripple="false"
            @click="isDialogOpen = false"
          >
            キャンセル
          </v-btn>
          <v-btn
            color="primary"
            :ripple="false"
            :disabled="!isValid"
            :loading="isSaving"
            @click="save"
          >
            保存
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import {
  ref, reactive, computed, watch, getCurrentInstance, onMounted,
} from '@vue/composition-api'
import ChargeSettingApi from '@/api/waiter/ChargeSetting'
import CheckoutConfigApi from '@/api/waiter/CheckoutConfig'

const emptyEditing = () => ({
  id: null,
  name: '',
  ratePercent: 20,
  customerCount: 1,
  taxCharge: 'cut',
  roundingMethod: 'round',
  roundingDigit: 0,
  roundingMultiple: 0,
})

export default {
  name: 'ChargeSettingsCard',
  setup() {
    const vm = getCurrentInstance()?.proxy
    const chargeSettings = ref([])
    const roundingMethods = ref({})
    const isDialogOpen = ref(false)
    const isSaving = ref(false)
    const editing = reactive(emptyEditing())
    const previewPrice = ref(4400)
    const preview = reactive({ chargeAmount: 0, total: 0 })

    const taxChargeItems = [
      { text: 'カット(そのまま加算)', value: 'cut' },
      { text: '通常(合計に消費税10%)', value: 'normal' },
    ]

    const roundingMethodItems = computed(() => (
      Object.entries(roundingMethods.value).map(([value, text]) => ({ text, value }))
    ))

    const isValid = computed(() => (
      !!editing.name && editing.ratePercent >= 0 && editing.customerCount >= 1
    ))

    const toPercent = rate => Math.round((Number(rate) || 0) * 100 * 100) / 100
    const formatPrice = n => Number(n || 0).toLocaleString('ja-JP')
    const taxChargeLabel = value => (taxChargeItems.find(i => i.value === value)?.text || value)
    const roundingLabel = chargeSetting => {
      const method = roundingMethods.value[chargeSetting.roundingMethod] || chargeSetting.roundingMethod
      if (chargeSetting.roundingMethod === 'ceil_to_multiple') return `${method}(${chargeSetting.roundingMultiple}円)`

      return `${method}(${10 ** Math.abs(chargeSetting.roundingDigit)}円)`
    }

    const loadChargeSettings = async () => {
      try {
        const res = await ChargeSettingApi.getChargeSettings()
        chargeSettings.value = (res?.data?.data || []).map(d => ({ ...d.attributes }))
      } catch (_) {
        chargeSettings.value = []
      }
    }

    const loadEnums = async () => {
      try {
        const res = await CheckoutConfigApi.getEnums()
        roundingMethods.value = res?.data?.roundingMethods || {}
      } catch (_) {
        roundingMethods.value = {}
      }
    }

    const openDialog = chargeSetting => {
      Object.assign(editing, emptyEditing())
      if (chargeSetting) {
        Object.assign(editing, {
          id: chargeSetting.id,
          name: chargeSetting.name,
          ratePercent: toPercent(chargeSetting.rate),
          customerCount: chargeSetting.customerCount,
          taxCharge: chargeSetting.taxCharge,
          roundingMethod: chargeSetting.roundingMethod,
          roundingDigit: chargeSetting.roundingDigit,
          roundingMultiple: chargeSetting.roundingMultiple,
        })
      }
      isDialogOpen.value = true
    }

    let previewSeq = 0
    let previewTimer = null
    const fetchPreview = async () => {
      previewSeq += 1
      const seq = previewSeq
      try {
        const res = await CheckoutConfigApi.calcTotal({
          price: Number(previewPrice.value) || 0,
          charge: {
            rate: (Number(editing.ratePercent) || 0) / 100,
            taxCharge: editing.taxCharge,
            roundingMethod: editing.roundingMethod,
            roundingDigit: editing.roundingDigit,
            roundingMultiple: editing.roundingMultiple,
            applied: true,
          },
        })
        if (seq !== previewSeq) return
        preview.chargeAmount = Number(res?.data?.chargeAmount ?? 0) || 0
        preview.total = Number(res?.data?.total ?? 0) || 0
      } catch (_) {
        preview.chargeAmount = 0
        preview.total = Number(previewPrice.value) || 0
      }
    }

    watch(
      () => [isDialogOpen.value, previewPrice.value, { ...editing }],
      () => {
        if (!isDialogOpen.value) return
        if (previewTimer) clearTimeout(previewTimer)
        previewTimer = setTimeout(fetchPreview, 200)
      },
      { deep: true },
    )

    const save = async () => {
      if (isSaving.value) return
      isSaving.value = true
      try {
        const params = {
          id: editing.id,
          name: editing.name,
          rate: (Number(editing.ratePercent) || 0) / 100,
          customerCount: editing.customerCount,
          taxCharge: editing.taxCharge,
          roundingMethod: editing.roundingMethod,
          roundingDigit: editing.roundingDigit,
          roundingMultiple: editing.roundingMultiple,
        }
        if (editing.id) {
          await ChargeSettingApi.updateChargeSetting(params)
          vm?.$toast?.success('チャージ設定を更新しました')
        } else {
          await ChargeSettingApi.createChargeSetting(params)
          vm?.$toast?.success('チャージ設定を作成しました')
        }
        isDialogOpen.value = false
        await loadChargeSettings()
      } catch (e) {
        vm?.$toast?.error(e?.response?.data?.message || '保存できませんでした')
      } finally {
        isSaving.value = false
      }
    }

    const destroy = async chargeSetting => {
      try {
        await ChargeSettingApi.deleteChargeSetting(chargeSetting.id)
        vm?.$toast?.success('チャージ設定を削除しました')
        await loadChargeSettings()
      } catch (_) {
        vm?.$toast?.error('削除できませんでした')
      }
    }

    onMounted(() => {
      loadChargeSettings()
      loadEnums()
    })

    return {
      chargeSettings,
      isDialogOpen,
      isSaving,
      editing,
      previewPrice,
      preview,
      taxChargeItems,
      roundingMethodItems,
      isValid,
      toPercent,
      formatPrice,
      taxChargeLabel,
      roundingLabel,
      openDialog,
      save,
      destroy,
    }
  },
}
</script>

<style scoped>
.charge-preview {
  border: 1px solid rgba(127, 127, 127, 0.32);
  border-radius: 8px;
}
</style>
